import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Users,
  Clock,
  CircleDollarSign,
  BarChart3,
  Calendar,
  FileText,
  Keyboard,
  Search,
  Database,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_SUMMARY = [
  {
    icon: Building2,
    title: "Client",
    desc: "A leading Finance Company, non-bank lender with a loan book of ₹1,850 crore",
  },
  {
    icon: Users,
    title: "Customers",
    desc: "Traders and small manufacturers in Maharashtra, Gujarat and Karnataka",
  },
  {
    icon: Clock,
    title: "Decision time",
    desc: "Six working days to five hours",
  },
  {
    icon: CircleDollarSign,
    title: "Cost to process a file",
    desc: "Down 40%",
  },
  {
    icon: BarChart3,
    title: "Loans overdue 90 days or more",
    desc: "Held at 2.7%",
  },
  {
    icon: Calendar,
    title: "Time frame",
    desc: "Nine months, from January 2026",
  },
];

const FinancialServicesCaseStudy = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/labs#insights");
    }
  };

  // Scroll tracking across the sticky track
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  /*
    Animation Timeline:
    0.0 -> 0.22: Overlay enters from bottom (100% -> 0%), opacity (0 -> 1)
    0.22 -> 0.75: Overlay stays PINNED over Hero section (0% -> 0%, opacity 1)
                  ("Hero section ke upr aakr kuch time ke liye ruka rhe")
    0.75 -> 0.98: Overlay slides UP out of view (0% -> -100%), opacity (1 -> 0)
    1.0+: Exits smoothly to next section
  */
  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#000000] flex flex-col">
      {/* Top Navigation Bar matching Screenshot 1 */}
      <header className="sticky top-0 z-50 w-full bg-[#000000] border-b border-white/[0.06] shadow-sm">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Simpel Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button matching Screenshot 1 */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Case Study / Finance Services
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Insights</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area: Sticky Scroll Pinning Track */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* Scroll Pinning Container (height allows smooth entry, pin duration, and exit) */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* 1. Underlying Hero Section Image */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/financial-services-hero.jpg"
                alt="Financial Services Case Study - Loan Approved"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* 2. Frosted Blur Overlay Section matching Screenshot 2 & 3
                Figma spec:
                - Rectangle 84
                - Colors: rgba(41, 67, 97, 0.4)
                - Background blur: Blur 23.9
            */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint & Backdrop Blur */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(41, 67, 97, 0.4)",
                  backdropFilter: "blur(23.9px)",
                  WebkitBackdropFilter: "blur(23.9px)",
                }}
              />

              {/* Foreground Content matching Screenshot 1 & 2 */}
              <div className="relative z-10 max-w-[1180px] flex flex-col items-center pt-4 sm:pt-0">
                {/* Main Heading with Highlighted Span */}
                <h1 className="text-[30px] sm:text-[42px] md:text-[50px] lg:text-[56px] font-bold leading-[1.2] sm:leading-[1.16] text-white tracking-tight">
                  A small-business lender cuts loan
                  <br className="hidden sm:inline" />{" "}
                  decisions from{" "}
                  <span className="text-[#38BDF8]">six days to five hours</span>{" "}
                  without
                  <br className="hidden sm:inline" />{" "}
                  loosening its credit standards
                </h1>

                {/* Subtitle */}
                <p className="mt-5 sm:mt-7 max-w-[860px] text-[14px] sm:text-[16px] md:text-[17px] leading-[24px] sm:leading-[26px] text-white/85">
                  How A leading Finance Company used AI to prepare the loan
                  file, and kept its credit officers in charge of the decision.
                </p>

                {/* Vertical Dashed Line with Downward Arrow */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-14 lg:mt-16 flex flex-col items-center cursor-pointer group select-none"
                  title="Scroll to continue"
                >
                  <svg
                    width="20"
                    height="126"
                    viewBox="0 0 20 126"
                    fill="none"
                    className="text-white/75 transition-transform duration-300 group-hover:translate-y-1.5"
                  >
                    <line
                      x1="10"
                      y1="0"
                      x2="10"
                      y2="114"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeDasharray="4 4"
                    />
                    <path
                      d="M5 110L10 118L15 110"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Case at a glance Section matching Screenshot 1 & 2
            Background: rgba(245, 250, 254, 1)
            Card: 1,312px width, 284px height, 8px radius, 1px border rgba(234, 242, 250, 1), bg rgba(255, 255, 255, 1)
        */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1312px] px-6 lg:px-0">
            {/* Heading row with gradient text and horizontal divider */}
            <div className="flex items-center justify-between mb-8 sm:mb-10">
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* Case Summary 2x3 Grid Container */}
            <div className="w-full rounded-[8px] border border-[#EAF2FA] bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {CASE_SUMMARY.map((item, idx) => {
                  const Icon = item.icon;
                  const isTopRow = idx < 3;
                  const isRightCol = idx % 3 === 2;
                  return (
                    <div
                      key={item.title}
                      className={`flex items-start gap-4 sm:gap-5 p-6 sm:p-7 lg:p-8 min-h-[142px] transition-colors duration-200 hover:bg-[#F8FAFC]/50 ${
                        isTopRow ? "lg:border-b border-[#EAF2FA]" : ""
                      } ${
                        !isRightCol ? "lg:border-r border-[#EAF2FA]" : ""
                      } ${
                        idx < 4 ? "md:max-lg:border-b border-[#EAF2FA]" : ""
                      } ${
                        idx % 2 === 0
                          ? "md:max-lg:border-r border-[#EAF2FA]"
                          : ""
                      } ${
                        idx < 5 ? "max-md:border-b border-[#EAF2FA]" : ""
                      }`}
                    >
                      {/* Icon Circle Badge */}
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                        <Icon className="h-6 w-6 stroke-[2]" />
                      </div>

                      {/* Content */}
                      <div className="flex flex-col gap-1.5 pt-0.5">
                        <h3 className="text-[15.5px] sm:text-[16px] font-bold text-[#0F172A] leading-tight">
                          {item.title}
                        </h3>
                        <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* The Context Section matching exact Screenshot 3
            Full-bleed background image with craftsman and Rectangle 85 gradient overlay
            Floating cards on top right over the craftsman scene
        */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#041421] min-h-[780px] lg:min-h-[810px] flex items-center"
        >
          {/* Background Image: the-context-bg.jpg across full section */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/the-context-bg.jpg"
              alt="The Context - MSME Entrepreneur"
              className="w-full h-full object-cover object-[72%_center] lg:object-right select-none"
            />
            {/* Rectangle 85: Linear Gradient overlay from rgba(4, 20, 33, 1) on the left to transparent on right */}
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                background:
                  "linear-gradient(90deg, rgba(4, 20, 33, 1) 0%, rgba(4, 20, 33, 0.98) 38%, rgba(4, 20, 33, 0.85) 52%, rgba(16, 82, 135, 0.25) 68%, rgba(16, 82, 135, 0) 84%)",
              }}
            />
          </div>

          {/* Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px] py-16 sm:py-20 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
              {/* Left Column (7 cols): Narrative & 55% Card */}
              <div className="lg:col-span-7 xl:col-span-7 flex flex-col gap-6 max-w-[580px]">
                {/* Title row */}
                <div className="flex items-center mb-2">
                  <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The Context
                  </h2>
                  <div className="hidden sm:block w-[140px] md:w-[200px] lg:w-[260px] h-[1px] bg-white/20 ml-6" />
                </div>

                <p className="text-[14px] sm:text-[15px] leading-[26px] text-[#E2E8F0]">
                  India's small businesses still cannot borrow what they need.
                  The Small Industries Development Bank of India puts the
                  sector's addressable credit gap at about ₹30 lakh crore, or 24%
                  (
                  <span className="underline decoration-white/40 underline-offset-2">
                    SIDBI, May 2025
                  </span>
                  ).
                </p>

                <p className="text-[14px] sm:text-[15px] leading-[26px] text-[#E2E8F0]">
                  Non-bank lenders are closing that gap. Their share of new loans
                  of up to ₹1 crore rose from 21% to 33% by value between FY20
                  and FY25 (
                  <span className="underline decoration-white/40 underline-offset-2">
                    MSME Pulse, June 2025
                  </span>
                  ). They are doing it in the hardest part of the market.
                </p>

                {/* 55% Stat Box matching Screenshot 3 */}
                <div className="my-1 rounded-[16px] border border-[#1E3A5F]/80 bg-[#0B2038]/85 backdrop-blur-md p-5 sm:p-6 shadow-xl max-w-[520px]">
                  <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
                    {/* Donut Gauge displaying 55% */}
                    <div className="relative flex h-[105px] w-[105px] shrink-0 items-center justify-center">
                      <svg
                        className="h-full w-full -rotate-90 transform"
                        viewBox="0 0 100 100"
                      >
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          stroke="rgba(255, 255, 255, 0.1)"
                          strokeWidth="8"
                          fill="transparent"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          stroke="#00D2EE"
                          strokeWidth="8"
                          strokeDasharray="138.2 251.3"
                          strokeLinecap="round"
                          fill="transparent"
                        />
                      </svg>
                      <span className="absolute text-[24px] sm:text-[26px] font-bold text-white tracking-tight">
                        55%
                      </span>
                    </div>

                    <div className="hidden sm:block h-14 w-[1px] bg-white/10 shrink-0" />

                    <p className="text-[13.5px] sm:text-[14px] leading-[22px] text-white/90">
                      of the small businesses that took a loan of up to ₹1 crore
                      in FY25 had never borrowed from a formal lender before.
                    </p>
                  </div>
                </div>

                <p className="text-[13.5px] sm:text-[14px] leading-[25px] text-[#94A3B8]">
                  The same report shows the risk. Loans overdue by 90 days or
                  more in this segment rose to 3.1% in March 2025, from 2.9% a
                  year earlier. The company had to grow in exactly this segment.
                </p>
              </div>

              {/* Right Column (5 cols): The 2 Floating Stats Cards floating in upper right over the craftsman scene matching Screenshot 3 */}
              <div className="lg:col-span-5 xl:col-span-5 flex flex-col items-center lg:items-end justify-start pt-4 lg:pt-0">
                <div className="flex flex-col gap-4 w-[230px] sm:w-[250px]">
                  {/* Card 1: MSME credit gap */}
                  <div className="w-full rounded-[14px] border border-white/20 bg-[#061A2C]/85 backdrop-blur-md p-4 sm:p-4.5 shadow-2xl">
                    <p className="text-[11px] font-medium text-white/75">
                      MSME credit gap
                    </p>
                    <h3 className="mt-1 text-[20px] sm:text-[22px] font-bold text-white tracking-tight">
                      ₹30 lakh crore
                    </h3>
                    <div className="mt-2.5 flex items-end justify-between">
                      <span className="text-[16px] font-semibold text-white/90">
                        24%
                      </span>
                      {/* Ascending Signal Bars */}
                      <div className="flex items-end gap-1 pb-0.5">
                        <span className="h-2 w-1.5 rounded-[1px] bg-white/70" />
                        <span className="h-3.5 w-1.5 rounded-[1px] bg-white/70" />
                        <span className="h-5 w-1.5 rounded-[1px] bg-white/70" />
                        <span className="h-6.5 w-1.5 rounded-[1px] bg-white/70" />
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Share of new loans bar chart */}
                  <div className="w-full rounded-[14px] border border-white/20 bg-[#061A2C]/85 backdrop-blur-md p-4 sm:p-4.5 shadow-2xl">
                    <div>
                      <p className="text-[12px] font-semibold text-white/90 leading-tight">
                        Share of new loans
                      </p>
                      <p className="text-[10.5px] text-white/60">
                        (up to ₹1 crore)
                      </p>
                    </div>

                    <div className="mt-4 flex items-end justify-around border-b border-white/15 pb-2 h-[76px]">
                      {/* FY20 Bar (21%) */}
                      <div className="flex flex-col items-center gap-1.5 h-full justify-end">
                        <span className="text-[11.5px] font-bold text-white">
                          21%
                        </span>
                        <div className="w-8 sm:w-9 h-[34px] rounded-t-[3px] bg-[#94A3B8]/80" />
                      </div>

                      {/* FY25 Bar (33%) */}
                      <div className="flex flex-col items-center gap-1.5 h-full justify-end">
                        <span className="text-[11.5px] font-bold text-white">
                          33%
                        </span>
                        <div className="w-8 sm:w-9 h-[58px] rounded-t-[3px] bg-[#CBD5E1]" />
                      </div>
                    </div>

                    <div className="mt-1.5 flex items-center justify-around text-[10.5px] font-medium text-white/60">
                      <span>FY20</span>
                      <span>FY25</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Challenge Section matching Screenshot 1 & 2
            Left: the-challenge.jpg image flush with left edge (Width 738.95px, Height 810.43px)
            Right: Narrative, 2 Reason cards (Figma specs: fill 285.93px width, 255.79px height, 12.06px radius, 1.72px border rgba(233, 237, 245, 1), bg rgba(245, 247, 250, 1)), and quote with vertical blue bar
            Background: rgba(255, 255, 255, 1)
        */}
        <section
          id="the-challenge"
          className="w-full relative flex flex-col lg:flex-row items-center overflow-hidden min-h-[810.43px]"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          {/* Left: Image flush to the left, width 738.95px, height 810.43px matching Screenshot 2 Figma specs */}
          <div className="w-full lg:w-[738.95px] lg:min-w-[738.95px] lg:max-w-[738.95px] h-[400px] sm:h-[540px] lg:h-[810.43px] shrink-0 overflow-hidden bg-slate-100">
            <img
              src="/images/insights/the-challenge.jpg"
              alt="The Challenge - Office desk with hourglass and laptop"
              className="w-full h-full object-cover object-center block select-none"
            />
          </div>

          {/* Right: Content Area */}
          <div className="flex-1 w-full flex flex-col justify-center px-6 sm:px-10 lg:pl-16 lg:pr-12 xl:pl-20 xl:pr-20 py-12 lg:py-10">
            <div className="max-w-[620px] w-full mx-auto lg:mx-0 flex flex-col">
              {/* Title row */}
              <div className="flex items-center gap-6 mb-6 sm:mb-7">
                <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The challenge
                </h2>
                <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Narrative Paragraphs */}
              <div className="flex flex-col gap-4 text-[13.5px] sm:text-[14px] leading-[24px] text-[#475569]">
                <p>
                  The company plan called for doubling its small-business lending
                  in three years. Its process could not carry that volume. A loan
                  file took six working days and passed through seven people
                  before a decision. Faster rivals were answering in hours, and
                  good borrowers were not waiting.
                </p>
                <p>
                  The obvious fix was an automated score that approves loans
                  without a person. The company leadership rejected it, for two
                  reasons:
                </p>
              </div>

              {/* Two Reason Cards matching Screenshot 4 Figma specs:
                  Width: Fill (285.93px), Height: Fixed (255.79px), Radius: 12.06px,
                  Border: 1.72px rgba(233, 237, 245, 1), Background: rgba(245, 247, 250, 1),
                  Padding: Top 11.2px, Right 8.61px, Bottom 15.5px, Left 18.95px
              */}
              <div className="mt-6 mb-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                {/* Card 01 */}
                <div
                  className="w-full rounded-[12px] flex flex-col justify-start"
                  style={{
                    backgroundColor: "rgba(245, 247, 250, 1)",
                    border: "1.72px solid rgba(233, 237, 245, 1)",
                    minHeight: "255.79px",
                    paddingTop: "20px",
                    paddingRight: "16px",
                    paddingBottom: "20px",
                    paddingLeft: "20px",
                  }}
                >
                  <span className="text-[22px] sm:text-[24px] font-bold text-[#2F6DF6]">
                    01
                  </span>
                  <h3 className="mt-2.5 text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                    Most applicants had no credit history.
                  </h3>
                  <p className="mt-2 text-[13px] leading-[20px] text-[#64748B]">
                    A score built on past borrowing has little to say about a
                    first-time borrower. It would turn away the customers the
                    lender most wanted.
                  </p>
                </div>

                {/* Card 02 */}
                <div
                  className="w-full rounded-[12px] flex flex-col justify-start"
                  style={{
                    backgroundColor: "rgba(245, 247, 250, 1)",
                    border: "1.72px solid rgba(233, 237, 245, 1)",
                    minHeight: "255.79px",
                    paddingTop: "20px",
                    paddingRight: "16px",
                    paddingBottom: "20px",
                    paddingLeft: "20px",
                  }}
                >
                  <span className="text-[22px] sm:text-[24px] font-bold text-[#2F6DF6]">
                    02
                  </span>
                  <h3 className="mt-2.5 text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                    The lender stays accountable.
                  </h3>
                  <p className="mt-2 text-[13px] leading-[20px] text-[#64748B]">
                    The company must be able to explain every approval and every
                    decline to its regulator and its auditors. A score it could
                    not explain was a risk in itself.
                  </p>
                </div>
              </div>

              {/* Bottom Callout / Quote with thick vertical blue line */}
              <div className="flex items-start gap-4 pt-1">
                <div className="w-[4.5px] h-[48px] rounded-full bg-[#2F6DF6] shrink-0 mt-0.5" />
                <p className="text-[17px] sm:text-[18px] lg:text-[19px] font-medium leading-[27px] sm:leading-[28px] text-[#1E293B]">
                  So the lender faced a hard choice between speed and judgement,
                  and its growth plan needed both.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Insights Section matching Screenshot 1 & 2
            Background: /images/insights/the-insights.jpg
            Left: Dark gradient overlay with "The Insights" title, subtitle, and narrative paragraphs
            Center/Right: 80% Donut chart overlaying file stack, connected to 4 clerical task items, and 25-minute judgement card at bottom-right
        */}
        <section
          id="the-insights"
          className="relative w-full overflow-hidden bg-[#041421] min-h-[760px] lg:min-h-[810px] flex items-center"
        >
          {/* Background Image: the-insights.jpg */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/the-insights.jpg"
              alt="The Insights - Loan files stack on office desk"
              className="w-full h-full object-cover object-[70%_center] lg:object-right select-none"
            />
            {/* Dark gradient overlay from left */}
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                background:
                  "linear-gradient(90deg, rgba(4, 20, 33, 1) 0%, rgba(4, 20, 33, 0.98) 36%, rgba(4, 20, 33, 0.86) 50%, rgba(16, 82, 135, 0.22) 68%, rgba(16, 82, 135, 0) 85%)",
              }}
            />
          </div>

          {/* Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px] py-16 sm:py-20 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
              {/* Left Column (6 cols): Text content */}
              <div className="lg:col-span-6 xl:col-span-6 flex flex-col max-w-[560px]">
                {/* Title row */}
                <div className="flex items-center mb-3">
                  <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The Insights
                  </h2>
                  <div className="hidden sm:block w-[140px] md:w-[200px] lg:w-[240px] h-[1px] bg-white/20 ml-6" />
                </div>

                {/* Subtitle */}
                <h3 className="text-[17px] sm:text-[19px] lg:text-[20px] font-bold text-white leading-snug mt-2 mb-4">
                  The delay was in preparing the file, not in deciding it.
                </h3>

                {/* Narrative Paragraphs */}
                <div className="flex flex-col gap-4 text-[13.5px] sm:text-[14px] leading-[25px] text-[#CBD5E1]">
                  <p>
                    Before proposing any technology, the joint team timed{" "}
                    <strong className="text-white font-bold">400</strong> loan
                    files from application to decision. About{" "}
                    <strong className="text-white font-bold">80%</strong> of the
                    elapsed time went on clerical work: collecting documents,
                    re-typing them, chasing missing pages and matching bank
                    statements to tax returns. The credit officer's actual
                    judgement took about{" "}
                    <strong className="text-white font-bold">25 minutes</strong>{" "}
                    per file.
                  </p>
                  <p className="text-[#94A3B8]">
                    That changed the question. The company did not need a machine
                    to make credit decisions. It needed its officers to stop
                    doing clerical work. Speed and judgement only looked like a
                    trade-off because both sat with the same people.
                  </p>
                </div>
              </div>

              {/* Right Column (6 cols): Visual Infographic Overlay */}
              <div className="lg:col-span-6 xl:col-span-6 relative flex flex-col justify-between min-h-[460px] lg:min-h-[520px]">
                {/* 1. Top Graphic: 80% Donut + Connector + 4 Tasks */}
                <div className="flex flex-col sm:flex-row items-center sm:items-center justify-center lg:justify-end gap-6 sm:gap-8 pt-4">
                  {/* Donut Chart */}
                  <div className="relative flex h-[170px] w-[170px] sm:h-[185px] sm:w-[185px] shrink-0 items-center justify-center">
                    <svg
                      className="h-full w-full rotate-[120deg] transform"
                      viewBox="0 0 160 160"
                    >
                      <defs>
                        <linearGradient
                          id="donutGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#00E5FF" />
                          <stop offset="100%" stopColor="#2979FF" />
                        </linearGradient>
                      </defs>
                      {/* Background Track */}
                      <circle
                        cx="80"
                        cy="80"
                        r="64"
                        stroke="rgba(255, 255, 255, 0.08)"
                        strokeWidth="13"
                        fill="transparent"
                      />
                      {/* 80% Active Arc: circumference = 2 * PI * 64 = 402.12; 80% = 321.7 */}
                      <circle
                        cx="80"
                        cy="80"
                        r="64"
                        stroke="url(#donutGradient)"
                        strokeWidth="13"
                        strokeDasharray="321.7 402.12"
                        strokeLinecap="round"
                        fill="transparent"
                      />
                    </svg>
                    {/* Centered text inside donut */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                      <span className="text-[30px] sm:text-[34px] font-bold text-white tracking-tight leading-none">
                        80%
                      </span>
                      <span className="mt-1.5 text-[10.5px] sm:text-[11px] font-medium leading-[13px] text-white/90">
                        of time spent on
                        <br />
                        clerical work
                      </span>
                    </div>
                  </div>

                  {/* Connecting lines & 4 Tasks List */}
                  <div className="relative flex items-center">
                    {/* SVG Connector Branch Line */}
                    <div className="hidden sm:block absolute -left-6 top-1/2 -translate-y-1/2 w-6 h-40 pointer-events-none">
                      <svg
                        className="w-full h-full"
                        viewBox="0 0 24 160"
                        fill="none"
                      >
                        <path
                          d="M0 80 H 12 V 16 H 24 M 12 58 H 24 M 12 102 H 24 M 12 144 H 24"
                          stroke="rgba(0, 210, 238, 0.3)"
                          strokeWidth="1.2"
                        />
                      </svg>
                    </div>

                    {/* 4 Task Items */}
                    <div className="flex flex-col gap-3.5 sm:gap-4">
                      {/* Item 1 */}
                      <div className="flex items-center gap-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00D2EE] shrink-0" />
                        <FileText className="h-4 w-4 text-white/75 shrink-0 stroke-[1.75]" />
                        <span className="text-[12.5px] sm:text-[13px] font-medium text-white/90 leading-tight">
                          Collecting documents
                        </span>
                      </div>

                      {/* Item 2 */}
                      <div className="flex items-center gap-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00D2EE] shrink-0" />
                        <Keyboard className="h-4 w-4 text-white/75 shrink-0 stroke-[1.75]" />
                        <span className="text-[12.5px] sm:text-[13px] font-medium text-white/90 leading-tight">
                          Re-typing them
                        </span>
                      </div>

                      {/* Item 3 */}
                      <div className="flex items-center gap-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00D2EE] shrink-0" />
                        <Search className="h-4 w-4 text-white/75 shrink-0 stroke-[1.75]" />
                        <span className="text-[12.5px] sm:text-[13px] font-medium text-white/90 leading-tight">
                          Chasing missing pages
                        </span>
                      </div>

                      {/* Item 4 */}
                      <div className="flex items-center gap-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00D2EE] shrink-0" />
                        <Database className="h-4 w-4 text-white/75 shrink-0 stroke-[1.75]" />
                        <span className="text-[12.5px] sm:text-[13px] font-medium text-white/90 leading-tight max-w-[170px]">
                          Matching bank statements to tax returns
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Bottom Right Card: 25 minutes of actual judgement per file */}
                <div className="flex justify-end pt-8 lg:pt-12">
                  <div className="w-[230px] sm:w-[250px] rounded-[12px] border border-[#2B7FFF]/35 bg-[#071629]/85 backdrop-blur-md px-4.5 py-3.5 shadow-2xl flex items-center gap-3.5">
                    <Clock className="h-8 w-8 text-white/90 shrink-0 stroke-[1.75]" />
                    <div className="flex flex-col">
                      <span className="text-[21px] sm:text-[22px] font-bold text-white tracking-tight leading-none">
                        25 minutes
                      </span>
                      <span className="mt-1 text-[11px] text-white/70 leading-tight">
                        of actual judgement per file
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Approach Section matching Screenshot 2026-10-13 171525.png
            Background: /images/insights/the-approach-bg.png (mountain range at sunset/sunrise with light trails)
            Heading: "The Approach" + horizontal divider line
            Subheading: "A practical plan, delivered in four moves."
            Intro: "The company's credit team led the work, with Simpel alongside. It ran in four moves over four months, from September to December 2025."
            4 Columns (Moves 01, 02, 03, 04) with vertical dividers
            Ascending trajectory curve with icon badges:
              - 01: Purple badge (Magnifier) + purple dot
              - 02: Blue badge (Document) + blue dot
              - 03: Teal/Cyan badge (Robot) + teal dot
              - 04: Orange badge (Shield check) + orange dot & arrow
            Bottom-left glassmorphic callout:
              - Users icon + "The lender also retrained 22 staff, who moved from data entry to reviewing the harder cases."
        */}
        <section
          id="the-approach"
          className="relative w-full overflow-hidden bg-white min-h-[820px] lg:min-h-[880px] pt-16 sm:pt-20 lg:pt-24 pb-16 sm:pb-20"
        >
          {/* Background Mountain Landscape at the bottom */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/the-approach-bg.png"
              alt="The Approach - Mountain landscape with light trails"
              className="w-full h-full object-cover object-bottom select-none"
            />
          </div>

          {/* Main Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px]">
            {/* 1. Header with brand gradient & horizontal line */}
            <div className="flex items-center justify-between mb-8 sm:mb-10">
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                The Approach
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* 2. Subheading & intro text */}
            <div className="max-w-[800px] mb-12 sm:mb-14">
              <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-bold text-[#0F172A] tracking-tight">
                A practical plan, delivered in four moves.
              </h3>
              <p className="mt-3 text-[14px] sm:text-[14.5px] leading-[22px] text-[#64748B]">
                The company's credit team led the work, with Simpel alongside. It
                ran in four moves over four months, from September to December
                2025.
              </p>
            </div>

            {/* 3. The 4 Moves (Cards / Columns) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 relative">
              {/* Move 01 */}
              <div className="flex flex-col pr-0 lg:pr-8 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#A855F7] leading-none">
                  01
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Find where the hours go.
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Using Simpel LOOP, the team linked the growth target to the
                  lending process, the systems behind it and the data available.
                  This produced the timing study and a ranked list of where AI
                  would pay back first.
                </p>

                {/* Vertical Divider for desktop */}
                <div className="hidden lg:block absolute right-0 top-1 h-[190px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Move 02 */}
              <div className="flex flex-col pl-0 lg:pl-8 pr-0 lg:pr-8 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#3B82F6] leading-none">
                  02
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Make the application paperless.
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Borrowers apply on a phone and can follow progress on
                  WhatsApp. With the borrower's consent, bank statements and GST
                  returns arrive as data instead of scanned pages.
                </p>

                {/* Vertical Divider for desktop */}
                <div className="hidden lg:block absolute right-0 top-1 h-[190px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Move 03 */}
              <div className="flex flex-col pl-0 lg:pl-8 pr-0 lg:pr-8 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#00D2EE] leading-none">
                  03
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Give file preparation to a digital worker.
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  An AI agent reads the documents, checks them against each
                  other, flags gaps and drafts a credit note with its reasons.
                  Clear cases go to an officer for a quick confirmation. Unclear
                  cases go to an officer for a full review, with the open
                  questions listed.
                </p>

                {/* Vertical Divider for desktop */}
                <div className="hidden lg:block absolute right-0 top-1 h-[190px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Move 04 */}
              <div className="flex flex-col pl-0 lg:pl-8">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#F97316] leading-none">
                  04
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Build the controls in from day one.
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Every step is logged: who saw what, what the agent
                  recommended, and who approved. The company's credit committee
                  sets the lending rules and changes them itself.
                </p>
              </div>
            </div>

            {/* 4. Ascending Trajectory Curve & Step Badges Container */}
            <div className="relative w-full mt-12 sm:mt-16 lg:mt-20 h-[190px] sm:h-[220px] lg:h-[250px]">
              {/* SVG Trajectory Path connecting 01 -> 02 -> 03 -> 04 to Summit */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                viewBox="0 0 1200 240"
                fill="none"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id="approachTrajectoryGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="30%" stopColor="#3B82F6" />
                    <stop offset="62%" stopColor="#00D2EE" />
                    <stop offset="100%" stopColor="#F97316" />
                  </linearGradient>
                </defs>

                {/* Subtle Glow behind curve */}
                <path
                  d="M 50,210 C 180,205 280,200 460,192 C 640,184 700,178 780,165 C 880,148 980,118 1070,95 C 1115,84 1145,68 1175,52"
                  stroke="url(#approachTrajectoryGradient)"
                  strokeWidth="6"
                  strokeOpacity="0.25"
                  strokeLinecap="round"
                />

                {/* Main Curved Path */}
                <path
                  d="M 50,210 C 180,205 280,200 460,192 C 640,184 700,178 780,165 C 880,148 980,118 1070,95 C 1115,84 1145,68 1175,52"
                  stroke="url(#approachTrajectoryGradient)"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />

                {/* Arrowhead at the top right */}
                <polygon
                  points="1165,42 1184,50 1172,62"
                  fill="#F97316"
                />

                {/* Vertical connecting dash lines from badges to dots */}
                <line
                  x1="180"
                  y1="165"
                  x2="180"
                  y2="204"
                  stroke="#A855F7"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />
                <line
                  x1="460"
                  y1="150"
                  x2="460"
                  y2="192"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />
                <line
                  x1="740"
                  y1="120"
                  x2="740"
                  y2="170"
                  stroke="#00D2EE"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />
                <line
                  x1="1020"
                  y1="55"
                  x2="1020"
                  y2="108"
                  stroke="#F97316"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />

                {/* Dots on trajectory curve */}
                <circle cx="180" cy="204" r="5" fill="#A855F7" />
                <circle cx="460" cy="192" r="5" fill="#3B82F6" />
                <circle cx="740" cy="170" r="5" fill="#00D2EE" />
                <circle cx="1020" cy="108" r="5" fill="#F97316" />
              </svg>

              {/* 4 Icon Badges Floating on trajectory */}
              {/* Badge 1 (Purple - Search) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "15%", top: "72%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(168,85,247,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/approach-icon-1.png"
                    alt="01 - Find where the hours go"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Badge 2 (Blue - Document) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "38%", top: "66%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(59,130,246,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/approach-icon-2.png"
                    alt="02 - Make the application paperless"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Badge 3 (Cyan - Robot) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "62%", top: "54%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(0,210,238,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/approach-icon-3.png"
                    alt="03 - Give file preparation to a digital worker"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Badge 4 (Orange - Shield) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "85%", top: "25%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(249,115,22,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/approach-icon-4.png"
                    alt="04 - Build the controls in from day one"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* 5. Bottom-Left Callout Card (Over the mountain clouds) */}
            <div className="mt-8 sm:mt-10 lg:mt-6">
              <div className="max-w-[480px] rounded-[14px] border border-white/25 bg-[#1E293B]/65 backdrop-blur-md px-5 py-4 shadow-xl flex items-center gap-4">
                <div className="flex items-center justify-center pr-3 border-r border-white/20 text-white/90 shrink-0">
                  <Users className="w-6 h-6 stroke-[1.75]" />
                </div>
                <p className="text-[13px] sm:text-[13.5px] leading-[20px] text-white/90">
                  The lender also{" "}
                  <strong className="font-bold text-white">
                    retrained 22 staff
                  </strong>
                  , who moved from data entry to reviewing the harder cases.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Impact Section matching Screenshots 1, 2, & 3
            Background: rgba(245, 250, 254, 1)
            Heading: "The Impact" + horizontal divider line
            Subheading: "Faster decisions, same discipline."
            Table: "Loan performance comparison" (Figma specs: Width 1,312px, Height 331px, Radius 11.2px, Border 1.21px solid rgba(208, 226, 255, 1))
            Quote card: (Figma specs: Width 1,297px, Height 131px, Radius 11.2px, Border 1.03px solid rgba(208, 226, 255, 1), Bg: rgba(229, 240, 255, 1), Padding: T 15.5px, R 34.45px, B 15.5px, L 37.03px, Gap 32.73px)
            Bottom note: "The last row matters most..."
        */}
        <section
          id="the-impact"
          className="w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1312px] px-6 lg:px-0">
            {/* 1. Header with brand gradient & horizontal divider */}
            <div className="flex items-center justify-between mb-8 sm:mb-10">
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                The Impact
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* 2. Subheading & intro text */}
            <div className="max-w-[850px] mb-8 sm:mb-10">
              <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-bold text-[#0F172A] tracking-tight">
                Faster decisions, same discipline.
              </h3>
              <p className="mt-3 text-[14px] sm:text-[14.5px] leading-[22px] text-[#64748B]">
                Within nine months of going live in January 2026, the company was
                deciding most loans the same day, with defaults no higher than
                before.
              </p>
            </div>

            {/* 3. Loan Performance Comparison Table matching Screenshot 1 & 3
                Figma specs: Width 1,312px, Height 331px, Radius 11.2px, Border 1.21px solid rgba(208, 226, 255, 1)
            */}
            <div
              className="w-full bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.02)]"
              style={{
                borderRadius: "11.2px",
                border: "1.21px solid rgba(208, 226, 255, 1)",
              }}
            >
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr
                      style={{
                        borderBottom: "1.21px solid rgba(208, 226, 255, 1)",
                      }}
                    >
                      <th className="py-3.5 px-6 text-[14px] font-bold text-[#0F172A] w-[45%]">
                        Measure
                      </th>
                      <th
                        className="py-3.5 px-6 text-[14px] font-semibold text-[#64748B] text-center w-[25%]"
                        style={{
                          backgroundColor: "rgba(245, 248, 255, 0.7)",
                          borderLeft: "1.21px solid rgba(208, 226, 255, 1)",
                          borderRight: "1.21px solid rgba(208, 226, 255, 1)",
                        }}
                      >
                        Before (2025)
                      </th>
                      <th
                        className="py-3.5 px-6 text-[14px] font-bold text-[#2B7FFF] text-center w-[30%]"
                        style={{
                          backgroundColor: "rgba(235, 243, 255, 0.8)",
                        }}
                      >
                        After (September 2026)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        measure: "Time from application to decision",
                        before: "6 working days",
                        after: "5 hours",
                      },
                      {
                        measure: "Share of files decided the same day",
                        before: "4%",
                        after: "71%",
                      },
                      {
                        measure: "Cost to process one file",
                        before: "₹2,900",
                        after: "₹1,750",
                      },
                      {
                        measure: "Files handled per officer per day",
                        before: "6",
                        after: "14",
                      },
                      {
                        measure: "Approval rate for first-time borrowers",
                        before: "31%",
                        after: "38%",
                      },
                      {
                        measure: "Loans overdue 90 days or more",
                        before: "2.8%",
                        after: "2.7%",
                      },
                    ].map((row, idx, arr) => (
                      <tr
                        key={row.measure}
                        className="transition-colors hover:bg-[#F8FAFC]/50"
                        style={{
                          borderBottom:
                            idx < arr.length - 1
                              ? "1.21px solid rgba(208, 226, 255, 0.7)"
                              : "none",
                        }}
                      >
                        <td className="py-3.5 px-6 text-[14px] font-medium text-[#334155]">
                          {row.measure}
                        </td>
                        <td
                          className="py-3.5 px-6 text-[14px] text-[#475569] text-center"
                          style={{
                            borderLeft: "1.21px solid rgba(208, 226, 255, 1)",
                            borderRight: "1.21px solid rgba(208, 226, 255, 1)",
                          }}
                        >
                          {row.before}
                        </td>
                        <td className="py-3.5 px-6 text-[18px] sm:text-[19px] font-bold text-[#2B7FFF] text-center">
                          {row.after}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 4. Quote Callout Card matching Screenshot 1 & 2 Figma specs
                Figma specs:
                Width: Fixed (1,297px)
                Height: Fixed (131px)
                Radius: 11.2px
                Border: 1.03px solid rgba(208, 226, 255, 1)
                Bg: rgba(229, 240, 255, 1)
                Padding: Top 15.5px, Right 34.45px, Bottom 15.5px, Left 37.03px
                Gap: 32.73px
            */}
            <div
              className="w-full mt-7 flex flex-col sm:flex-row items-center sm:items-center justify-between"
              style={{
                backgroundColor: "rgba(229, 240, 255, 1)",
                borderRadius: "11.2px",
                border: "1.03px solid rgba(208, 226, 255, 1)",
                paddingTop: "15.5px",
                paddingRight: "34.45px",
                paddingBottom: "15.5px",
                paddingLeft: "37.03px",
                gap: "32.73px",
              }}
            >
              {/* Left Quote Icon */}
              <div className="shrink-0 flex items-center justify-center">
                <svg
                  width="36"
                  height="28"
                  viewBox="0 0 40 32"
                  fill="none"
                  className="text-[#00A3FF]"
                >
                  <path
                    d="M0 18.667C0 8.36 6.8 0 16.4 0L17.6 3.6C11.6 4.933 8.8 8.667 8.8 13.067H17.6V32H0V18.667ZM22.4 18.667C22.4 8.36 29.2 0 38.8 0L40 3.6C34 4.933 31.2 8.667 31.2 13.067H40V32H22.4V18.667Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              {/* Vertical divider */}
              <div className="hidden sm:block h-12 w-[1px] bg-[#B8D3F8] shrink-0" />

              {/* Quote text and attribution */}
              <div className="flex-1 flex flex-col justify-center">
                <p className="text-[14px] sm:text-[14.5px] md:text-[15px] font-medium leading-[22px] sm:leading-[23px] text-[#1E293B]">
                  “We expected to trade speed for caution. Instead, our officers
                  spend the day on the cases that need them, and a first-time
                  borrower gets an answer before closing the shop for the
                  evening.”
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="w-5 h-[2px] bg-[#2B7FFF]" />
                  <span className="text-[12.5px] sm:text-[13px] font-medium text-[#2B7FFF]">
                    Chief Risk Officer, A leading Finance Company
                  </span>
                </div>
              </div>
            </div>

            {/* 5. Bottom note */}
            <p className="mt-5 text-[13.5px] sm:text-[14px] leading-[22px] text-[#64748B]">
              The last row matters most. The company became faster without taking
              on more bad loans, and stayed below the 3.1% overdue rate reported
              for this segment across lenders.
            </p>
          </div>
        </section>

        {/* What other lenders can take from this Section matching Screenshots 1, 2, & 3
            Figma Specs:
            Name: Lending takeaways
            Width: Fixed (1,440px)
            Height: Fixed (577px)
            Padding: Top 50.67px, Right 59.48px, Bottom 33.04px, Left 69.76px
            Colors: Linear gradient rgba(0, 47, 67, 1) to rgba(0, 34, 119, 1) over image
            Background Image: /images/insights/lending-takeaways-bg.png
            Header: "What other lenders can take from this" + horizontal divider line
            Subtitle: "Act now. Turn data into faster, safer lending."
            3 Cards:
              - 01: Measure before you automate. Find the real bottlenecks in your process.
              - 02: Automate preparation, not judgement. Give officers complete, verified files to decide faster.
              - 03: Make every decision explainable. Build trust with clear audit trails.
            Bottom callout line:
              - "The opportunity is now. As borrower data becomes easier to access, the advantage goes to lenders who can turn it into faster, explainable decisions."
        */}
        <section
          id="what-other-lenders-can-take"
          className="relative w-full overflow-hidden min-h-[577px] flex items-center bg-[#001D2D]"
        >
          {/* Background Image: lending-takeaways-bg.png */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/lending-takeaways-bg.png"
              alt="Lending takeaways - Office desk with laptop and charts"
              className="w-full h-full object-cover object-center select-none"
            />
            {/* Transparent dark gradient overlay blending rgba(0, 47, 67) and rgba(0, 34, 119) */}
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                background:
                  "linear-gradient(90deg, rgba(0, 47, 67, 0.65) 0%, rgba(0, 47, 67, 0.5) 40%, rgba(0, 34, 119, 0.35) 75%, rgba(0, 34, 119, 0.25) 100%)",
              }}
            />
          </div>

          {/* Content Container */}
          <div
            className="relative z-10 mx-auto max-w-[1440px] w-full flex flex-col justify-between"
            style={{
              paddingTop: "50.67px",
              paddingRight: "59.48px",
              paddingBottom: "33.04px",
              paddingLeft: "69.76px",
            }}
          >
            {/* 1. Header with brand gradient & horizontal divider */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                What other lenders can take from this
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-white/20 ml-8" />
            </div>

            {/* 2. Subheading */}
            <h3 className="text-[20px] sm:text-[22px] lg:text-[24px] font-bold text-white tracking-tight mb-8 sm:mb-10 drop-shadow-sm">
              Act now. Turn data into faster, safer lending.
            </h3>

            {/* 3. Three Takeaway Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
              {/* Card 01 */}
              <div className="rounded-[14px] border border-white/20 bg-[#06182C]/35 backdrop-blur-md p-6 sm:p-7 flex items-start gap-5 shadow-[0_8px_32px_rgba(0,0,0,0.25)] transition-all duration-300 hover:border-white/40 hover:bg-[#06182C]/45">
                <span className="text-[32px] sm:text-[36px] font-bold text-[#38BDF8] leading-none shrink-0 drop-shadow-sm">
                  01
                </span>
                <div className="h-12 w-[1px] bg-white/20 shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <h4 className="text-[16px] sm:text-[17px] font-bold text-white leading-snug drop-shadow-sm">
                    Measure before you automate.
                  </h4>
                  <p className="mt-2.5 text-[13px] leading-[20px] text-white/80">
                    Find the real bottlenecks in your process.
                  </p>
                </div>
              </div>

              {/* Card 02 */}
              <div className="rounded-[14px] border border-white/20 bg-[#06182C]/35 backdrop-blur-md p-6 sm:p-7 flex items-start gap-5 shadow-[0_8px_32px_rgba(0,0,0,0.25)] transition-all duration-300 hover:border-white/40 hover:bg-[#06182C]/45">
                <span className="text-[32px] sm:text-[36px] font-bold text-[#38BDF8] leading-none shrink-0 drop-shadow-sm">
                  02
                </span>
                <div className="h-12 w-[1px] bg-white/20 shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <h4 className="text-[16px] sm:text-[17px] font-bold text-white leading-snug drop-shadow-sm">
                    Automate preparation, not judgement.
                  </h4>
                  <p className="mt-2.5 text-[13px] leading-[20px] text-white/80">
                    Give officers complete, verified files to decide faster.
                  </p>
                </div>
              </div>

              {/* Card 03 */}
              <div className="rounded-[14px] border border-white/20 bg-[#06182C]/35 backdrop-blur-md p-6 sm:p-7 flex items-start gap-5 shadow-[0_8px_32px_rgba(0,0,0,0.25)] transition-all duration-300 hover:border-white/40 hover:bg-[#06182C]/45">
                <span className="text-[32px] sm:text-[36px] font-bold text-[#38BDF8] leading-none shrink-0 drop-shadow-sm">
                  03
                </span>
                <div className="h-12 w-[1px] bg-white/20 shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <h4 className="text-[16px] sm:text-[17px] font-bold text-white leading-snug drop-shadow-sm">
                    Make every decision explainable.
                  </h4>
                  <p className="mt-2.5 text-[13px] leading-[20px] text-white/80">
                    Build trust with clear audit trails.
                  </p>
                </div>
              </div>
            </div>

            {/* 4. Bottom Opportunity Callout */}
            <div className="mt-10 sm:mt-12 flex items-start gap-3.5">
              <div className="w-[2.5px] h-6 sm:h-7 bg-white shrink-0 mt-0.5 rounded-full" />
              <p className="text-[13.5px] sm:text-[14px] leading-[22px] text-white/80">
                <strong className="text-white font-bold">
                  The opportunity is now.
                </strong>{" "}
                As borrower data becomes easier to access, the advantage goes to
                lenders who can turn it into faster, explainable decisions.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FinancialServicesCaseStudy;
